<template>
  <div class="equipe-setor">
    <!-- Seletor de Setor -->
    <div class="setor-selector">
      <label for="setor-select">🏭 Selecionar Setor:</label>
      <select id="setor-select" v-model="setorSelecionado" @change="carregarEquipe" class="setor-select">
        <option value="">Selecione um setor</option>
        <option value="1">CORTE</option>
        <option value="2">COSTURA</option>
        <option value="3">ACABAMENTO</option>
        <option value="4">REVISÃO</option>
        <option value="5">EXPEDIÇÃO</option>
      </select>
    </div>

    <!-- ✅ TOGGLE DE VISUALIZAÇÃO -->
    <div v-if="setorSelecionado" class="visualizacao-toggle">
      <button 
        @click="tipoVisualizacao = 'setor'" 
        :class="{ active: tipoVisualizacao === 'setor' }"
        class="toggle-btn"
      >
        🏭 Por Setor
      </button>
      <button 
        @click="tipoVisualizacao = 'ordem'" 
        :class="{ active: tipoVisualizacao === 'ordem' }"
        class="toggle-btn"
      >
        📋 Por Ordem
      </button>
    </div>

    <!-- Loading -->
    <div v-if="carregando" class="loading">
      🔄 Carregando equipe...
    </div>

    <!-- Mensagem de Info -->
    <div v-else-if="mensagemInfo" class="mensagem-info" :class="tipoMensagem">
      <div class="info-icon">
        <span v-if="tipoMensagem === 'info'">ℹ️</span>
        <span v-else-if="tipoMensagem === 'warning'">⚠️</span>
        <span v-else>❌</span>
      </div>
      <div class="info-content">
        <h4>{{ tipoMensagem === 'info' ? 'Informação' : 'Aviso' }}</h4>
        <p>{{ mensagemInfo }}</p>
        <small>Setor: {{ setorSelecionado }}</small>
      </div>
    </div>

    <!-- ✅ VISUALIZAÇÃO POR SETOR (EXISTENTE) -->
    <div v-else-if="tipoVisualizacao === 'setor' && funcionarios.length > 0">
      <!-- Resumo da Equipe -->
      <div v-if="resumoEquipe" class="resumo-equipe">
        <div class="resumo-card">
          <h4>📊 Resumo da Equipe</h4>
          <div class="resumo-stats">
            <div class="stat-item">
              <span class="stat-numero">{{ resumoEquipe.total_funcionarios }}</span>
              <span class="stat-texto">Total</span>
            </div>
            <div class="stat-item ativo">
              <span class="stat-numero">{{ resumoEquipe.funcionarios_trabalhando }}</span>
              <span class="stat-texto">Trabalhando</span>
            </div>
            <div class="stat-item finalizado">
              <span class="stat-numero">{{ resumoEquipe.funcionarios_finalizados }}</span>
              <span class="stat-texto">Finalizados</span>
            </div>
            <div class="stat-item tempo">
              <span class="stat-numero">{{ formatarTempoHorasMinutos(Math.round(resumoEquipe.tempo_total_trabalhado * 60)) }}</span>
              <span class="stat-texto">Tempo Total</span>
              <span class="stat-decimal">{{ formatarTempoDecimal(resumoEquipe.tempo_total_trabalhado) }}</span>
              <span class="stat-minutos">{{ Math.round(resumoEquipe.tempo_total_trabalhado * 60) }} min</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Lista de Funcionários -->
      <div class="funcionarios-lista">
        <div 
          v-for="funcionario in funcionarios" 
          :key="funcionario.id"
          class="funcionario-card"
          @click="selecionarFuncionario(funcionario)"
        >
          <div class="funcionario-header">
            <div class="funcionario-info">
              <h3>{{ funcionario.nome }}</h3>
              <span class="funcionario-id">ID: {{ funcionario.id }}</span>
            </div>
            <div class="funcionario-status" :class="funcionario.status_trabalho">
              {{ formatarStatus(funcionario.status_trabalho) }}
            </div>
          </div>

          <div class="funcionario-detalhes">
            <div class="detalhe-item">
              <span class="label">🏭 Setor:</span>
              <span class="valor">{{ funcionario.setor_atual || 'N/A' }}</span>
            </div>
            <div class="detalhe-item">
              <span class="label">📋 Ordem Atual:</span>
              <span class="valor">{{ funcionario.ordem_atual || 'Nenhuma' }}</span>
            </div>
            <div class="detalhe-item">
              <span class="label">⏰ Tempo Trabalhado:</span>
              <span class="valor">{{ formatarTempoDecimal(funcionario.tempo_trabalhado_hoje) }}</span>
            </div>
            <div class="detalhe-item">
              <span class="label">📊 Produção Hoje:</span>
              <span class="valor">{{ funcionario.producao_hoje || 0 }} peças</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ✅ NOVA: VISUALIZAÇÃO POR ORDEM -->
    <div v-else-if="tipoVisualizacao === 'ordem'">
      <div v-if="ordensComFuncionarios.length === 0" class="estado-vazio">
        <div class="vazio-icon">📭</div>
        <h3>Nenhuma ordem com funcionários ativos</h3>
        <p>Não há funcionários trabalhando em ordens no momento neste setor.</p>
      </div>
      
      <div v-else class="ordens-lista">
        <div 
          v-for="ordem in ordensComFuncionarios" 
          :key="ordem.id"
          class="ordem-funcionarios-card"
        >
          <div class="ordem-header">
            <div class="ordem-info">
              <h3>📋 Ordem #{{ ordem.id_ordem }}</h3>
              <div class="ordem-detalhes">
                <span class="cliente" v-if="ordem.cliente">👤 {{ ordem.cliente }}</span>
                <span class="produto" v-if="ordem.produto">📦 {{ ordem.produto }}</span>
              </div>
            </div>
            <div class="ordem-stats">
              <span class="funcionarios-count">👥 {{ ordem.funcionarios.length }} funcionário{{ ordem.funcionarios.length !== 1 ? 's' : '' }}</span>
              <span class="tempo-total">⏱️ {{ calcularTempoTotalOrdem(ordem) }}</span>
            </div>
          </div>
          
          <div class="funcionarios-grid">
            <div 
              v-for="funcionario in ordem.funcionarios" 
              :key="funcionario.id"
              class="funcionario-mini"
              @click="selecionarFuncionario(funcionario)"
            >
              <div class="funcionario-info">
                <div class="funcionario-nome">
                  <strong>{{ funcionario.nome }}</strong>
                  <span class="funcionario-id-mini">ID: {{ funcionario.id }}</span>
                </div>
                <div class="funcionario-dados">
                  <span class="setor">🏭 {{ funcionario.setor_atual }}</span>
                  <span class="tempo">⏱️ {{ formatarTempoDecimal(funcionario.tempo_trabalhado_hoje) }}</span>
                  <span class="producao">📊 {{ funcionario.producao_hoje || 0 }} peças</span>
                </div>
                <div class="funcionario-status-mini" :class="funcionario.status_trabalho">
                  {{ formatarStatus(funcionario.status_trabalho) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Estado Vazio -->
    <div v-else-if="!carregando && !mensagemInfo && funcionarios.length === 0" class="estado-vazio">
      <div class="vazio-icon">😴</div>
      <h3>Nenhum funcionário encontrado</h3>
      <p>Selecione um setor para ver a equipe.</p>
    </div>
  </div>
</template>
<script>
import { producaoService } from '@/services/producaoService';

export default {
  name: 'EquipeSetor',
  
  data() {
    return {
      funcionarios: [],
      dadosSetor: null,
      resumoEquipe: null,
      estatisticasSetor: null,
      carregando: false,
      setorSelecionado: '2',
      mensagemInfo: null,
      tipoMensagem: null,
      // ✅ NOVOS DADOS:
      tipoVisualizacao: 'setor', // 'setor' ou 'ordem'
      ordensComFuncionarios: []
    };
  },
  
  mounted() {
    this.carregarEquipe();
  },
  
  methods: {
    async carregarEquipe() {
      if (!this.setorSelecionado) {
        this.limparDados();
        return;
      }

      try {
        this.carregando = true;
        this.mensagemInfo = null;
        console.log('🔄 Carregando equipe do setor:', this.setorSelecionado);

        const response = await producaoService.buscarEquipeSetor(this.setorSelecionado);

        if (response.status === 'sucesso') {
          this.dadosSetor = response.setor;
          this.resumoEquipe = response.equipe;
          this.funcionarios = response.funcionarios || [];
          this.estatisticasSetor = response.estatisticas_setor;

          console.log('✅ Equipe carregada:', this.funcionarios.length, 'funcionários');
          
          // ✅ CARREGAR ORDENS COM FUNCIONÁRIOS
          await this.carregarOrdensComFuncionarios();
        } 
        else if (response.status === 'info') {
          console.log('ℹ️ Setor sem expediente ativo:', response.message);
          this.limparDados();
          this.mensagemInfo = response.message;
          this.tipoMensagem = 'info';
          this.$toast?.info?.(response.message);
        } 
        else {
          throw new Error(response.message || 'Erro ao carregar equipe');
        }

      } catch (error) {
        console.error('❌ Erro ao carregar equipe:', error);
        this.$toast?.error?.('Erro ao carregar dados da equipe');
        this.limparDados();
        this.mensagemInfo = error.message || 'Erro ao carregar dados da equipe';
        this.tipoMensagem = 'error';
      } finally {
        this.carregando = false;
      }
    },

    // ✅ NOVO MÉTODO: ORGANIZAR FUNCIONÁRIOS POR ORDEM
    async carregarOrdensComFuncionarios() {
      if (this.funcionarios.length === 0) {
        this.ordensComFuncionarios = [];
        return;
      }

      const ordensMap = new Map();
      
      this.funcionarios.forEach(funcionario => {
        // Usar ordem_atual do funcionário ou criar uma genérica
        const ordemId = funcionario.ordem_atual || `SEM-ORDEM-${funcionario.id}`;
        
        if (!ordensMap.has(ordemId)) {
          ordensMap.set(ordemId, {
            id: ordemId,
            id_ordem: ordemId,
            cliente: funcionario.cliente_atual || 'Cliente não informado',
            produto: funcionario.produto_atual || 'Produto não informado',
            funcionarios: []
          });
        }
        
        ordensMap.get(ordemId).funcionarios.push(funcionario);
      });
      
      this.ordensComFuncionarios = Array.from(ordensMap.values())
        .sort((a, b) => {
          // Ordens reais primeiro, depois as genéricas
          if (a.id.startsWith('SEM-ORDEM') && !b.id.startsWith('SEM-ORDEM')) return 1;
          if (!a.id.startsWith('SEM-ORDEM') && b.id.startsWith('SEM-ORDEM')) return -1;
          return a.id.localeCompare(b.id);
        });
      
      console.log('📋 Ordens com funcionários:', this.ordensComFuncionarios.length);
    },

    // ✅ NOVO MÉTODO: CALCULAR TEMPO TOTAL DA ORDEM
    calcularTempoTotalOrdem(ordem) {
      const totalHoras = ordem.funcionarios.reduce((total, func) => {
        return total + (func.tempo_trabalhado_hoje || 0);
      }, 0);
      
      return this.formatarTempoDecimal(totalHoras);
    },

    selecionarFuncionario(funcionario) {
      console.log('👤 Funcionário selecionado:', funcionario.nome);
      // Aqui você pode abrir um modal ou navegar para detalhes
      alert(`Funcionário: ${funcionario.nome}\nSetor: ${funcionario.setor_atual}\nStatus: ${this.formatarStatus(funcionario.status_trabalho)}\nTempo hoje: ${this.formatarTempoDecimal(funcionario.tempo_trabalhado_hoje)}`);
    },

    limparDados() {
      this.dadosSetor = null;
      this.resumoEquipe = null;
      this.funcionarios = [];
      this.estatisticasSetor = null;
      this.mensagemInfo = null;
      this.tipoMensagem = null;
      this.ordensComFuncionarios = [];
    },

    // Métodos utilitários existentes...
    formatarStatus(status) {
      const statusMap = {
        'trabalhando': 'Trabalhando',
        'pausado': 'Pausado',
        'finalizado': 'Finalizado',
        'ausente': 'Ausente'
      };
      return statusMap[status] || status || 'N/A';
    },

    formatarTempoDecimal(horas) {
      if (!horas || horas === 0) return '0.00h';
      return `${Number(horas).toFixed(2)}h`;
    },

    formatarTempoHorasMinutos(totalMinutos) {
      if (!totalMinutos || totalMinutos === 0) return '0h 0min';
      
      const horas = Math.floor(totalMinutos / 60);
      const minutos = totalMinutos % 60;
      
      if (horas === 0) {
        return `${minutos}min`;
      } else if (minutos === 0) {
        return `${horas}h`;
      } else {
        return `${horas}h ${minutos}min`;
      }
    }
  }
}
</script>
<style scoped>
/* Estilos existentes... */

/* ✅ NOVOS ESTILOS PARA TOGGLE E VISUALIZAÇÃO POR ORDEM */
.visualizacao-toggle {
  display: flex;
  gap: 10px;
  margin: 20px 0;
  justify-content: center;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
}

.toggle-btn {
  padding: 10px 20px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
}

.toggle-btn:hover {
  background: #e3f2fd;
  border-color: #3498db;
}

.toggle-btn.active {
  background: #3498db;
  color: white;
  border-color: #3498db;
  box-shadow: 0 2px 8px rgba(52, 152, 219, 0.3);
}

.visualizacao-ordem {
  margin-top: 20px;
}

.ordens-lista {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.ordem-funcionarios-card {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  border-left: 4px solid #3498db;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
}

.ordem-funcionarios-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.ordem-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ecf0f1;
}

.ordem-info h3 {
  margin: 0 0 5px 0;
  color: #2c3e50;
}

.ordem-detalhes {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.ordem-detalhes .cliente,
.ordem-detalhes .produto {
  font-size: 13px;
  color: #7f8c8d;
}

.ordem-stats {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 5px;
}

.funcionarios-count,
.tempo-total {
  font-size: 12px;
  color: #7f8c8d;
  background: white;
  padding: 4px 8px;
  border-radius: 12px;
}

.funcionarios-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 15px;
}

.funcionario-mini {
  background: white;
  padding: 15px;
  border-radius: 8px;
  border-left: 3px solid #27ae60;
  cursor: pointer;
  transition: all 0.3s ease;
}

.funcionario-mini:hover {
  background: #e8f5e8;
  transform: translateX(3px);
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.funcionario-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.funcionario-nome {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.funcionario-nome strong {
  color: #2c3e50;
  font-size: 14px;
}

.funcionario-id-mini {
  font-size: 11px;
  color: #95a5a6;
  background: #ecf0f1;
  padding: 2px 6px;
  border-radius: 10px;
}

.funcionario-dados {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.funcionario-dados .setor,
.funcionario-dados .tempo,
.funcionario-dados .producao {
  font-size: 12px;
  color: #7f8c8d;
}

.funcionario-status-mini {
  align-self: flex-start;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
}

.funcionario-status-mini.trabalhando {
  background: #e8f5e8;
  color: #27ae60;
}

.funcionario-status-mini.pausado {
  background: #fff3e0;
  color: #f57c00;
}

.funcionario-status-mini.finalizado {
  background: #e3f2fd;
  color: #1976d2;
}

.funcionario-status-mini.ausente {
  background: #ffebee;
  color: #d32f2f;
}

.estado-vazio {
  text-align: center;
  padding: 60px 20px;
  color: #7f8c8d;
}

.vazio-icon {
  font-size: 48px;
  margin-bottom: 15px;
}

.estado-vazio h3 {
  margin: 0 0 10px 0;
  color: #2c3e50;
}

.estado-vazio p {
  margin: 0;
  font-size: 16px;
}

/* Responsividade */
@media (max-width: 768px) {
  .visualizacao-toggle {
    flex-direction: column;
  }
  
  .toggle-btn {
    text-align: center;
  }
  
  .ordem-header {
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }
  
  .ordem-stats {
    align-items: flex-start;
    flex-direction: row;
    gap: 10px;
  }
  
  .funcionarios-grid {
    grid-template-columns: 1fr;
  }
  
  .funcionario-nome {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }
}
</style>